import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import {
  DISCOUNT_CODE_EXTERNAL_LINKS,
  LEGAL_JURIDISCH_LINK_NL,
  NEWSLETTER_PAGE_SOURCE,
  legalLink,
  CountryCode,
} from '~shared/constants';
import { VoucherType } from '~shared/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ExponeaService } from '~shared/services/exponea.service';
import { getCountryDomainCode } from '~server/utils/country-detection.util';
import { environment } from '~env/environment';
import { SafeHtml } from '@angular/platform-browser';
import { animate, style, transition, trigger } from '@angular/animations';
import { GtmDatalayerService } from '~shared/services/gtm-datalayer.service';
import { GATrackingEventType, GaTrackingEventAction, GaTrackingEventName } from '~shared/modules/ga-tracking/constants';
import { COLOR_OPTIONS } from '~app/modules/storyblok/constants';

const resendSubmitTimer = 3000;

@Component({
  selector: 'app-newsletter-v2',
  templateUrl: './newsletter-v2.component.html',
  styleUrls: ['./newsletter-v2.component.scss'],
  animations: [
    trigger('showLegalText', [
      transition(':enter', [style({ opacity: '0' }), animate('.15s ease-in', style({ opacity: '1' }))]),
      transition(':leave', [style({ opacity: '1' }), animate('.15s ease-out', style({ opacity: '0' }))]),
    ]),
  ],
})
export class NewsletterV2Component implements OnInit {
  @Input() source: NEWSLETTER_PAGE_SOURCE;
  @Input() title?: string;
  @Input() voucherType?: VoucherType;
  @Input() amount?: string;
  @Input() color?: string;
  @Input() isSubsidiaryAccount?: boolean;
  @Input() NLDiscount?: string;

  blueVoucherImage: string;
  whiteVoucherImage: string;
  legalLink: string;
  public form: FormGroup;
  isMobile = false;
  isFormSubmitted = false;
  isLoading = false;
  resend = false;
  pageSlug = '';
  market: string;
  showLegalText = false;

  constructor(
    private fb: FormBuilder,
    private exponeaService: ExponeaService,
    private gtmDatalayerService: GtmDatalayerService,
    private cd: ChangeDetectorRef
  ) {
    this.market = getCountryDomainCode();
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });
    this.blueVoucherImage = `${environment.cdnBaseUrl()}/images/voucher illustration-1.svg`;
    this.whiteVoucherImage = `${environment.cdnBaseUrl()}/images/voucher illustration.svg`;
    this.legalLink = legalLink[this.market];
  }

  ngOnInit(): void {
    this.pageSlug = this.getPageSlug();
    // This discount depends on NL_Discount_5 FF
    this.amount = (!this.title && this.market == CountryCode.Netherlands) ? this.NLDiscount : this.amount
  }

  onSubmit(): void {
    if (this.form.valid) {
      this.postNewsletterInfo()
    }
  }

  resendEmail(): void {
    this.postNewsletterInfo(true)
  }

  private postNewsletterInfo(isResend: boolean = false): void {
    if (isResend) {
      this.resend = true;
    }

    this.isLoading = true;
    this.exponeaService
      .sendNewsletterConsentData(this.form.value.email, this.source, this.pageSlug)
      .toPromise()
      .then(() => {
        this.isFormSubmitted = true;
        this.fireSuscriptionEvent();
      })
      .catch(() => {
        this.isFormSubmitted = false;
        if (isResend) {
          this.resend = false;
        }
      })
      .finally(() => {
        this.isLoading = false;
        this.cd.detectChanges();
      })
  }

  getPageSlug(): string {
    return window.location.pathname.split('/').pop() || '';
  }

  colorSelector(): string {
    return COLOR_OPTIONS[this.color] || COLOR_OPTIONS['white'];
  }

  onFocus(): void {
    this.showLegalText = true;
  }

  getPromoText(promoText: string, promoLinkText: string, style: string): string {
    if (promoText && promoLinkText && DISCOUNT_CODE_EXTERNAL_LINKS[this.market]) {
      const promoLink = `<a ${style} href=${DISCOUNT_CODE_EXTERNAL_LINKS[this.market]}>${promoLinkText}</a>`;
      return ` ${promoText} ${promoLink}.`;
    }

    return '';
  }

  getNewsletterContent(
    legalText: string,
    policyText: string,
    promoTextTranslation: string,
    promoLinkText: string
  ): SafeHtml {
    const style = this.color === 'lightBlue' ? 'style="color: inherit; text-decoration: underline;"' : '';
    const metroLink = `<a ${style} href=${LEGAL_JURIDISCH_LINK_NL}>METRO Markets GmbH</a>`;
    const legalLink = `<a ${style} href=${this.legalLink}>${policyText}</a>`;
    const promoText = this.getPromoText(promoTextTranslation, promoLinkText, style);
    return `${
      this.market === 'nl' ? legalText.replace('METRO Markets GmbH', metroLink) : legalText
    } ${legalLink}.${promoText}`;
  }

  initializeNewsletterView(): void {
    this.gtmDatalayerService.gtagEvent({
      event: GATrackingEventType.NEWSLETTER_VIEWED,
      GA_eventname: GaTrackingEventName.Interaction,
      action: GaTrackingEventAction.NEWSLETTER_VIEWED,
    });
  }

  fireSuscriptionEvent(): void {
    this.gtmDatalayerService.gtagEvent({
      event: GATrackingEventType.NEWSLETTER_SUSCRIPTION,
      GA_eventname: GaTrackingEventName.Interaction,
      action: GaTrackingEventAction.NEWSLETTER_SUSCRIPTION,
      label: this.source,
    });
  }
}
